import React, { useEffect, useState } from "react";
import Map, { Marker, NavigationControl, ScaleControl, FullscreenControl } from "react-map-gl";
import { Box, Flex, Text, Spinner, useColorModeValue } from "@chakra-ui/react";
import useAxios from "../utils/useAxios";

interface GatewayGPS {
  accuracy: number;
  altitude: number;
  latitude: number;
  longitude: number;
  created_at: string;
}

const GatewayMap: React.FC<{ gatewayId: string }> = ({ gatewayId }) => {
  const api = useAxios();
  const [gpsData, setGpsData] = useState<GatewayGPS | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const cardBg = useColorModeValue("white", "gray.900");

  useEffect(() => {
    if (!gatewayId) return;

    setLoading(true);
    setError(false);
    api
      .get(`monitoring/gateway-gps/${gatewayId}/`)
      .then((res) => {
        // Adjust for large integers on arrival if necessary or handle in rendering
        // Assuming API sends 590020660 and we need 59.0020660
        // Data might come as integers directly
        const data = res.data;
        if (data && data.latitude && data.longitude) {
          // Normalize coordinates if they are integers > 180 (for lat) or > 360 (for long)
          if (Math.abs(data.latitude) > 90) data.latitude /= 10000000;
          if (Math.abs(data.longitude) > 180) data.longitude /= 10000000;

          setGpsData(data);
        } else {
          setGpsData(null);
        }
        setLoading(false);
      })
      .catch((err) => {
        // console.error("Failed to fetch gateway GPS", err);
        // Treat 404 or other errors as "No GPS data"
        setGpsData(null);
        setError(true);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayId]);

  if (loading)
    return (
      <Flex justify="center" p={4}>
        <Spinner />
      </Flex>
    );

  if (!gpsData) {
    return (
      <Box mt={8} bg={cardBg} p={6} rounded="md" boxShadow="md" w="full">
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Gateway Location
        </Text>
        <Text color="gray.500">No GPS data available for this gateway.</Text>
      </Box>
    );
  }

  return (
    <Box mt={8} bg={cardBg} p={6} rounded="md" boxShadow="md" w="full">
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Gateway Location
      </Text>
      <Box h="400px" w="100%" borderRadius="md" overflow="hidden" position="relative">
        <Map
          mapLib={import("mapbox-gl")}
          initialViewState={{
            longitude: gpsData.longitude,
            latitude: gpsData.latitude,
            zoom: 13,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        >
          <Marker
            longitude={gpsData.longitude}
            latitude={gpsData.latitude}
            anchor="bottom"
            color="red"
          >
            <div style={{ fontSize: "24px", color: "red" }}>📍</div>
          </Marker>

          <NavigationControl position="top-right" />
          <FullscreenControl position="top-right" />
          <ScaleControl />
        </Map>
      </Box>
      <Text mt={2} fontSize="sm" color="gray.500">
        GPS data updated: {new Date(gpsData.created_at).toLocaleString()} (Accuracy:{" "}
        {gpsData.accuracy / 10}m)
      </Text>
    </Box>
  );
};

export default GatewayMap;
